// src/components/mensagens/UniversalWhatsAppModal.jsx
// ============================================================================
// 🎯 COMPONENTE: UniversalWhatsAppModal - VERSÃO ULTRA PREMIUM
// 📋 Função: Modal universal para enviar WhatsApp com design INCRÍVEL
// 📱 Responsivo: Adaptado para mobile e desktop
// 🔌 V5: Conectado ao hook + Catálogo oficial de templates
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Phone,
  Search,
  Send,
  Check,
  AlertCircle,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  Clock,
  Sparkles,
  Zap,
  Star,
  Award,
  TrendingUp,
  Globe,
  Smartphone,
  Copy,
  CheckCircle,
  UserPlus
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { RichMessageEditor } from '../RichMessageEditor';
// 🆕 V4: Importar o hook de mensagens (substitui a prop onSend)
import { useMensagemAcoes } from '../../features/mensagens/hooks/useMensagemAcoes';
// 🆕 V5: Importar catálogo oficial de templates (única fonte de verdade)
import {
  getTemplates,
  generateTemplateMessage,
} from '../../features/mensagens/api/templates';
import {
  normalizePhoneBR,
  formatPhoneDisplay,
  getInitials,
  isValidPhone,
  filterClientsByRelevance
} from './helpers';

/**
 * 🎯 Componente de modal universal de WhatsApp - VERSÃO PREMIUM
 * 🔌 V5: Conectado ao hook + catálogo oficial de templates
 */
export const UniversalWhatsAppModal = ({
  isOpen,
  onClose,
  onClientCreated,
  primaryColor = '#10b981',
  clients = [],
  database
}) => {
  const isMobile = useIsMobile();
  
  // 🆕 V4: Conectar ao hook de envio
  const { sendMessage, actionInProgress: isSendingFromHook } = useMensagemAcoes();
  
  // 🆕 V5: Templates do catálogo oficial
  const templatesCatalog = useMemo(() => getTemplates(), []);
  
  const [step, setStep] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [customPhone, setCustomPhone] = useState('');
  const [customName, setCustomName] = useState('');
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [template, setTemplate] = useState('padrao');
  const [phoneError, setPhoneError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [recentClients, setRecentClients] = useState([]);
  const [hoveredOption, setHoveredOption] = useState(null);
  
  // ==========================================================================
  // 🆕 ESTADOS PARA CADASTRO RÁPIDO
  // ==========================================================================
  const [showCadastro, setShowCadastro] = useState(true);
  const [cadastroRealizado, setCadastroRealizado] = useState(false);
  
  // Carregar clientes recentes
  useEffect(() => {
    try {
      const saved = localStorage.getItem('recent_whatsapp_clients');
      if (saved) {
        setRecentClients(JSON.parse(saved).slice(0, 3));
      }
    } catch (error) {
      console.error('Erro ao carregar clientes recentes:', error);
    }
  }, []);

  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients.slice(0, 5);
    return filterClientsByRelevance(clients, searchTerm).slice(0, 5);
  }, [clients, searchTerm]);

  // 🆕 V5: Usar catálogo oficial para gerar mensagem do template
  useEffect(() => {
    const name = selectedClient?.name || customName || '';
    setMessage(generateTemplateMessage(template, { name }));
  }, [template, selectedClient, customName]);

  const validatePhoneInput = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 13) {
      setPhoneError('Número inválido (mínimo 10 dígitos)');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const handleClose = () => {
    setStep(1);
    setSelectedClient(null);
    setCustomPhone('');
    setCustomName('');
    setMessage('');
    setSearchTerm('');
    setTemplate('padrao');
    setPhoneError('');
    onClose();
  };

  const saveToRecent = (client) => {
    try {
      const updated = [client, ...recentClients.filter(c => c.id !== client.id)].slice(0, 3);
      setRecentClients(updated);
      localStorage.setItem('recent_whatsapp_clients', JSON.stringify(updated));
    } catch (error) {
      console.error('Erro ao salvar cliente recente:', error);
    }
  };

  // ==========================================================================
  // 🆕 V4: handleSend agora usa o hook sendMessage diretamente
  // ==========================================================================
  const handleSend = async () => {
    if (step === 1) return;

    const phone = selectedClient?.phone || customPhone;
    if (!validatePhoneInput(phone)) return;

    const name = selectedClient?.name || customName || 'Cliente';
    const finalMessage = message || generateTemplateMessage(template, { name });

    setIsSending(true);

    try {
      const result = await sendMessage({
        phone,
        name,
        message: finalMessage,
        clientId: selectedClient?.id,
        template,
      });

      if (result.success && selectedClient) {
        saveToRecent(selectedClient);
      }

      if (result.success) {
        handleClose();
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setIsSending(false);
    }
  };
  
 // ==========================================================================
// 🆕 FUNÇÃO PARA CADASTRO RÁPIDO (CORRIGIDA)
// ==========================================================================
const handleQuickCadastro = async () => {
  try {
    console.log('🔵 Iniciando cadastro rápido...');
    
    if (!customPhone) {
      console.error('❌ Telefone não informado');
      return;
    }

    if (!database) {
      console.error('❌ Database não disponível nas props');
      if (window.showToast) {
        window.showToast('Erro: Database não disponível', 'error');
      }
      return;
    }

    const novoCliente = {
      id: `client_${Date.now()}`,
      name: customName?.trim() || 'Cliente WhatsApp',
      phone: customPhone,
      email: '',
      status: 'Novo',
      temperature: 'Morno',
      type: 'Lead',
      source: 'WhatsApp',
      createdAt: new Date().toISOString(),
      tags: ['whatsapp']
    };
    
    console.log('📝 Criando novo cliente:', novoCliente);
    
    if (database.createClient) {
      await database.createClient(novoCliente);
      
      console.log('✅ Cliente cadastrado com sucesso!');
      
      if (onClientCreated) {
        onClientCreated(novoCliente);
      }
      
      setCadastroRealizado(true);
      setShowCadastro(false);
      
      if (window.showToast) {
        window.showToast('Cliente cadastrado com sucesso!', 'success');
      }
      
      setTimeout(() => setCadastroRealizado(false), 3000);
    } else {
      console.error('❌ Método createClient não disponível no database');
    }
    
  } catch (error) {
    console.error('❌ Erro ao cadastrar:', error);
    if (window.showToast) {
      window.showToast('Erro ao cadastrar cliente', 'error');
    }
  }
};
  
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[10000] p-3"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className={`
            relative
            bg-white/90 backdrop-blur-xl
            rounded-3xl
            border border-white/20
            shadow-2xl
            w-full mx-auto
            max-h-[90vh] overflow-y-auto
            ${isMobile ? 'max-w-full p-4' : 'max-w-2xl p-6'}
          `}
          style={{
            boxShadow: `0 25px 50px -12px ${primaryColor}40`,
            borderColor: `${primaryColor}20`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Efeito de brilho animado no fundo */}
          <motion.div
            animate={{
              background: [
                `radial-gradient(circle at 20% 20%, ${primaryColor}20, transparent 50%)`,
                `radial-gradient(circle at 80% 80%, ${primaryColor}20, transparent 50%)`,
                `radial-gradient(circle at 20% 20%, ${primaryColor}20, transparent 50%)`
              ]
            }}
            transition={{ duration: 10, repeat: Infinity }}
            className="absolute inset-0 pointer-events-none"
          />

          {/* Header Premium */}
          <div className="relative z-10 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div
                  whileHover={{ rotate: 180, scale: 1.1 }}
                  transition={{ type: "spring" }}
                  className={`
                    relative
                    bg-gradient-to-br from-green-500 to-emerald-600
                    rounded-2xl
                    flex items-center justify-center
                    shadow-xl
                    ${isMobile ? 'p-2.5' : 'p-3'}
                  `}
                  style={{
                    boxShadow: `0 10px 20px -5px ${primaryColor}80`
                  }}
                >
                  <MessageSquare size={isMobile ? 20 : 24} className="text-white" />
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 bg-white/20 rounded-2xl"
                  />
                </motion.div>

                <div>
                  <motion.h2
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    className={`
                      font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent
                      ${isMobile ? 'text-lg' : 'text-xl'}
                    `}
                  >
                    {step === 1 ? 'Enviar WhatsApp' : 'Nova Mensagem'}
                  </motion.h2>
                  <motion.p
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="text-xs text-gray-500"
                  >
                    {step === 1 
                      ? 'Escolha uma opção para começar'
                      : 'Personalize sua mensagem com estilo'}
                  </motion.p>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                className="p-2 hover:bg-black/5 rounded-xl transition-colors"
              >
                <X size={isMobile ? 18 : 20} className="text-gray-500" />
              </motion.button>
            </div>

            {step === 2 && (
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '50%' }}
                className="h-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mt-4"
                style={{ width: '50%' }}
              />
            )}
          </div>

          {/* Step 1: Escolher opção */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {recentClients.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1">
                    <Clock size={12} />
                    CONTINUAR CONVERSA
                  </h3>
                  <div className="space-y-2">
                    {recentClients.map((client, index) => (
                      <motion.button
                        key={client.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * index }}
                        whileHover={{ scale: 1.02, x: 5 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setSelectedClient(client);
                          setStep(2);
                        }}
                        className="w-full group relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
                        <div className="relative p-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-medium shadow-lg">
                            {getInitials(client.name)}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-semibold text-gray-900">{client.name}</p>
                            <p className="text-xs text-gray-500">{client.phone}</p>
                          </div>
                          <ChevronRight size={18} className="text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all" />
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1">
                <Sparkles size={12} />
                NOVA CONVERSA
              </h3>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onHoverStart={() => setHoveredOption('cliente')}
                onHoverEnd={() => setHoveredOption(null)}
                onClick={() => setStep(2)}
                className="w-full group relative overflow-hidden"
              >
                <motion.div
                  animate={{
                    background: hoveredOption === 'cliente'
                      ? 'linear-gradient(135deg, #3b82f620, #8b5cf620)'
                      : 'linear-gradient(135deg, #3b82f610, #8b5cf610)'
                  }}
                  className="absolute inset-0"
                />
                <div className="relative p-6 border-2 border-dashed border-blue-200 rounded-2xl hover:border-blue-400 transition-all">
                  <div className="flex items-center gap-4">
                    <motion.div
                      animate={{ rotate: hoveredOption === 'cliente' ? 360 : 0 }}
                      transition={{ duration: 0.5 }}
                      className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-lg"
                    >
                      <User size={24} className="text-white" />
                    </motion.div>
                    <div className="flex-1 text-left">
                      <h3 className="font-bold text-gray-900 text-lg">Cliente existente</h3>
                      <p className="text-sm text-gray-500">Buscar na lista de clientes</p>
                    </div>
                    <motion.div
                      animate={{ x: hoveredOption === 'cliente' ? 5 : 0 }}
                      className="p-2 bg-blue-100 rounded-full"
                    >
                      <ChevronRight size={20} className="text-blue-600" />
                    </motion.div>
                  </div>
                </div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onHoverStart={() => setHoveredOption('numero')}
                onHoverEnd={() => setHoveredOption(null)}
                onClick={() => setStep(2)}
                className="w-full group relative overflow-hidden"
              >
                <motion.div
                  animate={{
                    background: hoveredOption === 'numero'
                      ? 'linear-gradient(135deg, #10b98120, #05966920)'
                      : 'linear-gradient(135deg, #10b98110, #05966910)'
                  }}
                  className="absolute inset-0"
                />
                <div className="relative p-6 border-2 border-dashed border-green-200 rounded-2xl hover:border-green-400 transition-all">
                  <div className="flex items-center gap-4">
                    <motion.div
                      animate={{ rotate: hoveredOption === 'numero' ? 360 : 0 }}
                      transition={{ duration: 0.5 }}
                      className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg"
                    >
                      <Phone size={24} className="text-white" />
                    </motion.div>
                    <div className="flex-1 text-left">
                      <h3 className="font-bold text-gray-900 text-lg">Número personalizado</h3>
                      <p className="text-sm text-gray-500">Enviar para qualquer contato</p>
                    </div>
                    <motion.div
                      animate={{ x: hoveredOption === 'numero' ? 5 : 0 }}
                      className="p-2 bg-green-100 rounded-full"
                    >
                      <ChevronRight size={20} className="text-green-600" />
                    </motion.div>
                  </div>
                </div>
              </motion.button>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-4"
              >
                <CheckCircle size={12} className="text-green-500" />
                <span>Criptografado de ponta a ponta</span>
              </motion.div>
            </motion.div>
          )}

          {/* Step 2: Preencher dados */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-5"
            >
              <motion.button
                whileHover={{ x: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2 group"
              >
                <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                Voltar
              </motion.button>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <Search size={14} className="text-gray-400" />
                  Buscar cliente existente
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Nome, telefone ou email..."
                    className="w-full pl-9 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-offset-2 outline-none transition-all text-sm"
                    style={{ 
                      focusRing: `2px solid ${primaryColor}40`,
                      borderColor: searchTerm ? primaryColor : undefined
                    }}
                  />
                </div>

                {filteredClients.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 space-y-1 max-h-40 overflow-y-auto rounded-xl bg-gray-50 p-1"
                  >
                    {filteredClients.map((client, index) => (
                      <motion.button
                        key={client.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.01, x: 2 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => {
                          setSelectedClient(client);
                          setCustomPhone('');
                          setCustomName('');
                          setSearchTerm('');
                        }}
                        className={`
                          w-full p-2 text-left rounded-lg transition-all flex items-center gap-2
                          ${selectedClient?.id === client.id 
                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 shadow-sm' 
                            : 'hover:bg-white'
                          }
                        `}
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-xs shadow-md">
                          {getInitials(client.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate text-sm">{client.name}</p>
                          <p className="text-xs text-gray-500">{formatPhoneDisplay(client.phone)}</p>
                        </div>
                        {selectedClient?.id === client.id && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check size={12} className="text-white" />
                          </motion.div>
                        )}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </div>

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-white text-xs text-gray-400 flex items-center gap-1">
                    <Sparkles size={12} />
                    ou
                  </span>
                </div>
              </div>

              <div className="space-y-4 bg-gray-50/50 rounded-xl p-4 border border-gray-100">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Telefone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={customPhone}
                      onChange={(e) => {
                        setCustomPhone(e.target.value);
                        setSelectedClient(null);
                        validatePhoneInput(e.target.value);
                      }}
                      placeholder="(11) 99999-9999"
                      className={`
                        w-full pl-9 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-offset-2 outline-none transition-all text-sm
                        ${phoneError 
                          ? 'border-red-300 bg-red-50/50 focus:ring-red-200' 
                          : 'border-gray-200 focus:border-green-300'
                        }
                      `}
                      style={{ focusRing: `2px solid ${phoneError ? '#ef4444' : primaryColor}40` }}
                    />
                  </div>
                  {phoneError && (
                    <motion.p
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-red-500 mt-1 flex items-center gap-1"
                    >
                      <AlertCircle size={12} />
                      {phoneError}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Nome <span className="text-gray-400 text-xs">(opcional)</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      placeholder="Nome do contato"
                      className="w-full pl-9 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-offset-2 outline-none transition-all text-sm"
                      style={{ focusRing: `2px solid ${primaryColor}40` }}
                    />
                  </div>
                </div>
              </div>

              {/* 🆕 V5: Templates do catálogo oficial */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Escolha um template
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {templatesCatalog.map((t) => {
                    // Mapear ícones do catálogo para componentes Lucide
                    const iconMap = {
                      MessageSquare, Award, Star, Clock,
                      FileText, Home, Bell, PlusSquare,
                      TrendingDown, FileCheck, CheckCircle,
                      MessageCircle, Cake,
                    };
                    const Icon = iconMap[t.icon] || MessageSquare;
                    const isSelected = template === t.id;
                    
                    // Cores por categoria
                    const categoryColors = {
                      atendimento: 'from-blue-500 to-indigo-500',
                      negociacao: 'from-green-500 to-emerald-500',
                      visitas: 'from-purple-500 to-pink-500',
                      followup: 'from-amber-500 to-orange-500',
                      imoveis: 'from-indigo-500 to-purple-500',
                      documentacao: 'from-teal-500 to-cyan-500',
                      pos_venda: 'from-cyan-500 to-blue-500',
                      relacionamento: 'from-pink-500 to-rose-500',
                    };
                    const gradient = categoryColors[t.category] || 'from-blue-500 to-indigo-500';

                    return (
                      <motion.button
                        key={t.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setTemplate(t.id)}
                        className={`
                          relative p-3 rounded-xl border-2 transition-all overflow-hidden
                          ${isSelected 
                            ? `border-transparent bg-gradient-to-r ${gradient} text-white shadow-lg` 
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                          }
                        `}
                      >
                        {isSelected && (
                          <motion.div
                            layoutId="template-bg"
                            className="absolute inset-0 bg-gradient-to-r"
                            style={{
                              background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`
                            }}
                          />
                        )}
                        <div className="relative z-10 flex flex-col items-center gap-1">
                          <Icon size={20} className={isSelected ? 'text-white' : 'text-gray-600'} />
                          <span className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-gray-700'}`}>
                            {t.name}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Sua mensagem
                </label>
                <RichMessageEditor
                  value={message}
                  onChange={setMessage}
                  placeholder="Digite sua mensagem..."
                  height={isMobile ? '150px' : '200px'}
                />
              </div>

              {(selectedClient || customPhone) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`
                    p-4 rounded-xl border flex items-center gap-3
                    ${!phoneError && (selectedClient || customPhone)
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                    }
                  `}
                >
                  <div className={`
                    p-2 rounded-lg
                    ${!phoneError && (selectedClient || customPhone) ? 'bg-green-100' : 'bg-red-100'}
                  `}>
                    <Smartphone size={18} className={!phoneError ? 'text-green-600' : 'text-red-600'} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedClient 
                        ? selectedClient.name
                        : customName || 'Contato'
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedClient 
                        ? formatPhoneDisplay(selectedClient.phone)
                        : formatPhoneDisplay(customPhone) || 'Número não informado'
                      }
                    </p>
                  </div>
                  {!phoneError && (selectedClient || customPhone) && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center"
                    >
                      <Check size={14} className="text-white" />
                    </motion.div>
                  )}
                </motion.div>
              )}
              
              {!selectedClient && customPhone && customPhone.replace(/\D/g, '').length >= 10 && showCadastro && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
                >
                  <div className="flex items-start gap-3">
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="p-2 bg-blue-100 rounded-lg"
                    >
                      <UserPlus size={18} className="text-blue-600" />
                    </motion.div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900 mb-1">
                        Deseja cadastrar este contato?
                      </p>
                      <p className="text-xs text-blue-700 mb-3">
                        {customName || 'Cliente'} • {formatPhoneDisplay(customPhone)}
                      </p>
                      <div className="flex gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('🔵 Cadastrando cliente...');
                            handleQuickCadastro();
                          }}
                          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-medium shadow-lg hover:shadow-xl transition-all flex items-center gap-1"
                        >
                          <UserPlus size={14} className="animate-pulse" />
                          <span>Cadastrar agora</span>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('⚪ Apenas enviar mensagem');
                            setShowCadastro(false);
                          }}
                          className="px-4 py-2 bg-white border-2 border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 transition-all flex items-center gap-1"
                        >
                          <span>Apenas enviar</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {cadastroRealizado && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="mt-4 p-3 bg-green-50 rounded-xl border border-green-200 flex items-center gap-2"
                >
                  <div className="p-1 bg-green-100 rounded-full">
                    <CheckCircle size={16} className="text-green-600" />
                  </div>
                  <span className="text-sm text-green-700">Cliente cadastrado com sucesso!</span>
                </motion.div>
              )}

              <div className="flex gap-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setStep(1)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all text-sm border border-gray-200"
                >
                  Cancelar
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSend}
                  disabled={(!selectedClient && !customPhone) || phoneError || isSending || isSendingFromHook}
                  className={`
                    flex-1 px-4 py-3 rounded-xl font-medium transition-all text-sm
                    flex items-center justify-center gap-2 relative overflow-hidden
                    ${(!selectedClient && !customPhone) || phoneError || isSending || isSendingFromHook
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-lg'
                    }
                  `}
                  style={{ 
                    background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
                    color: 'white'
                  }}
                >
                  {(isSending || isSendingFromHook) ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Enviar Mensagem</span>
                      <motion.div
                        animate={{ x: ['-100%', '200%'] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      />
                    </>
                  )}
                </motion.button>
              </div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center text-xs text-gray-400 flex items-center justify-center gap-1"
              >
                <Zap size={12} className="text-yellow-500" />
                Mensagens enviadas via WhatsApp Web
              </motion.p>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * 🎯 Botão flutuante com design premium
 */
export const UniversalFAB = ({ onClick, isMobile }) => {
  return (
    <motion.button
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      whileHover={{ scale: 1.1, rotate: 5 }}
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      className={`
        fixed bottom-6 right-6
        bg-gradient-to-r from-green-600 to-emerald-600
        text-white rounded-full
        shadow-2xl
        flex items-center justify-center
        z-40
        transition-all
        ${isMobile 
          ? 'w-14 h-14' 
          : 'px-6 py-3 gap-2'
        }
      `}
      style={{
        boxShadow: '0 20px 25px -5px rgba(16, 185, 129, 0.3), 0 10px 10px -5px rgba(16, 185, 129, 0.2)'
      }}
    >
      <MessageSquare size={isMobile ? 24 : 20} />
      {!isMobile && (
        <>
          <span className="font-medium">Nova Mensagem</span>
          <span className="text-xs opacity-75 ml-2">Ctrl+N</span>
        </>
      )}
    </motion.button>
  );
};

/**
 * 🎯 Barra de ações rápidas
 */
export const QuickActionsBar = ({ onSelect, primaryColor = '#10b981' }) => {
  const actions = [
    { id: 'cliente', label: 'Cliente existente', icon: User },
    { id: 'novo', label: 'Número novo', icon: Phone },
    { id: 'recente', label: 'Recentes', icon: Clock }
  ];

  return (
    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
      {actions.map(action => {
        const Icon = action.icon;
        return (
          <motion.button
            key={action.id}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onSelect(action.id)}
            className="flex items-center gap-1 px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all whitespace-nowrap"
          >
            <Icon size={14} style={{ color: primaryColor }} />
            <span className="text-xs font-medium text-gray-700">{action.label}</span>
          </motion.button>
        );
      })}
    </div>
  );
};

export default UniversalWhatsAppModal;